import { useMemo } from 'react';
import { components, MenuListProps, OptionProps, SingleValueProps } from 'react-select';
import Select from '@/library/components/form/inputs/select';

type ColorOption = { value: string };

type ColorFieldSelectProps = {
  value?: string | ColorOption | null;
  onChange: (value: string | null) => void;
  label?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  optionsLength?: number;
  type?: 'event' | 'status';
  error?: string | null;
};

const getOptions = (optionsLength: number): ColorOption[] => {
  const colors: ColorOption[] = [];

  for (let index = 1; index <= optionsLength; index++) {
    colors.push({ value: `color_${index}` });
  }

  return colors;
};

const getColorValue = (value: string | ColorOption | null | undefined): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value.value;
};

const ColorSelect = ({
  value,
  onChange,
  label,
  className,
  placeholder = '',
  disabled,
  optionsLength = 7,
  type = 'status',
  error
}: ColorFieldSelectProps) => {
  const options = useMemo(() => getOptions(optionsLength), [optionsLength]);

  const selected = useMemo(() => {
    const colorValue = getColorValue(value);
    return colorValue ? (options.find(option => option.value === colorValue) ?? null) : null;
  }, [options, value]);

  const componentsConfig = useMemo(() => {
    const ColorSingleValue = (props: SingleValueProps<ColorOption>) => (
      <components.SingleValue className="colorpicker-selector" {...props}>
        {props.data.value ? <div className={`color-option ${type}-${props.data.value}`} /> : null}
      </components.SingleValue>
    );

    const ColorOptionRow = (props: OptionProps<ColorOption>) => (
      <components.Option {...props}>
        <div className="color-options-wrap">
          <div className={`color-option ${type}-${props.data.value}`} />
        </div>
      </components.Option>
    );

    const ColorMenuList = (props: MenuListProps<ColorOption>) => (
      <components.MenuList className="colorpicker-selector" {...props} />
    );

    return { MenuList: ColorMenuList, Option: ColorOptionRow, SingleValue: ColorSingleValue };
  }, [type]);

  return (
    <Select<ColorOption>
      className={className}
      selectClassName="colorpicker-selector"
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      isAsync={false}
      isClearable
      options={options}
      getOptionValue={option => option.value}
      getOptionLabel={option => option.value}
      components={componentsConfig}
      value={selected}
      onChange={option => onChange((option as ColorOption | null)?.value ?? null)}
      error={error}
    />
  );
};

export default ColorSelect;
